import { Card } from '@/components/shared/Card'
import type { EverflowOfferRef } from '@/types/api'

interface EverflowOffersTileProps {
  offers: EverflowOfferRef[]
  offerName?: string
}

/**
 * Compact reference tile listing the Everflow offer IDs the affiliate should be running
 * for the current offer — one per tier. IDs only (no payout/rate), per the affiliate
 * security invariant. Purely informational so affiliates know which links to promote.
 */
export function EverflowOffersTile({ offers, offerName }: EverflowOffersTileProps) {
  if (!offers || offers.length === 0) return null

  return (
    <Card className="mb-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-text-primary">Your Everflow offers</h3>
          <p className="text-[11px] text-text-muted mt-0.5">
            Offer IDs to run{offerName ? ` for ${offerName}` : ''} — one per tier
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {offers.map((o, i) => {
          // Display tiers as T1..T(x) regardless of internal code (O4T1, TU1, …):
          // trailing digits of the tier code, else sequential position. The optional letter
          // suffix is load-bearing — Freecash has T1A/T1B/T1C as distinct rates, and without
          // it they all failed the match and fell back to array position, labelling them by
          // where they happened to sit in the list.
          const tierNum = /t(\d+[a-z]?)$/i.exec(o.tier_code)?.[1].toUpperCase() ?? String(i + 1)
          return (
            <div
              key={o.tier_code}
              className="flex items-center gap-2 rounded-lg bg-gray-50 border border-gray-200 px-3 py-1.5"
            >
              {/* Tier number is the primary signal for the affiliate. */}
              <span className="font-semibold text-sm text-text-primary">T{tierNum}</span>
              {/* Offer ID is secondary/reference — de-emphasized. */}
              <span className="font-mono text-xs text-text-placeholder">#{o.offer_id}</span>
            </div>
          )
        })}
      </div>
    </Card>
  )
}
